import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { endpoints, setAuthToken } from "@/lib/api";

/**
 * AuthContext — real user authentication.
 * user === null → still checking
 * user === false → not authenticated
 * user === {...} → authenticated
 */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [artists, setArtists] = useState([]); // artists this user can operate in the workspace
    const [venues, setVenues] = useState([]); // venues this user can operate in the workspace

    const loadMyArtists = useCallback(async () => {
        try {
            const d = await endpoints.authMyArtists();
            setArtists(d.artists || []);
        } catch {
            setArtists([]);
        }
    }, []);

    const loadMyVenues = useCallback(async () => {
        try {
            const d = await endpoints.authMyVenues();
            setVenues(d.venues || []);
        } catch {
            setVenues([]);
        }
    }, []);

    const checkAuth = useCallback(async () => {
        try {
            const d = await endpoints.authMe();
            setUser(d.user);
            await Promise.all([loadMyArtists(), loadMyVenues()]);
        } catch (err) {
            if (err?.response?.status !== 401) {
                // eslint-disable-next-line no-console
                console.warn("[auth] /me failed:", err?.message);
            }
            setUser(false);
            setArtists([]);
            setVenues([]);
        }
    }, [loadMyArtists, loadMyVenues]);

    // One-time cleanup: `pm_fan_activated` used to control Fan Passport
    // visibility client-side. That authority has moved to the persistent
    // `has_fan_passport` field on the user account. Purge the stale flag
    // so it cannot mislead any lingering code path.
    useEffect(() => {
        try { localStorage.removeItem("pm_fan_activated"); } catch {}
    }, []);

    useEffect(() => {
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setUser(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        const d = await endpoints.authLogin({ email, password });
        if (d.access_token) setAuthToken(d.access_token);
        setUser(d.user);
        await Promise.all([loadMyArtists(), loadMyVenues()]);
        return d.user;
    };

    const register = async (payload) => {
        const d = await endpoints.authRegister(payload);
        if (d.access_token) setAuthToken(d.access_token);
        setUser(d.user);
        await Promise.all([loadMyArtists(), loadMyVenues()]);
        return d.user;
    };

    const activateFanPassport = async () => {
        const d = await endpoints.authActivateFanPassport();
        if (d?.user) setUser(d.user);
        return d?.user;
    };

    const logout = async () => {
        try { await endpoints.authLogout(); } catch {}
        setAuthToken(null);
        setUser(false);
        setArtists([]);
        setVenues([]);
    };

    const setUserAndArtists = async (u) => {
        setUser(u);
        await Promise.all([loadMyArtists(), loadMyVenues()]);
    };

    return (
        <AuthContext.Provider value={{ user, artists, venues, login, register, logout, checkAuth, setUserAndArtists, loadMyArtists, loadMyVenues, activateFanPassport }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
